import { useState } from "react";
import axios from "axios";
import { CheckCircle2, MailX } from "lucide-react";

// "" (empty) is a valid, intentional value: the production build uses relative
// /api URLs behind Caddy (same origin). `||` would treat "" as unset and send
// the browser to localhost:8001; `??` only falls back when it is truly unset.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? "http://localhost:8001";

/**
 * Standalone, unauthenticated page at /unsubscribe, reached from an
 * unsubscribe link in an outbound marketing/outreach email.
 *
 * POST /compliance/unsubscribe was fully implemented - it records the
 * request, adds the email to the suppression list is_suppressed() checks
 * before every outbound send, and audit-logs the action - but had no
 * frontend caller anywhere, so a recipient had no way to actually reach
 * it and self-serve opt out.
 */
export default function UnsubscribePage() {
  const params = new URLSearchParams(window.location.search);
  const [email, setEmail] = useState(params.get("email") || "");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const handleUnsubscribe = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${BACKEND_URL}/api/compliance/unsubscribe`, {
        data: { email: email.trim().toLowerCase(), source: "unsubscribe_link" },
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-container" data-testid="unsubscribe-page">
      <div className="login-panel glass-panel">
        <div className="brand-lockup" style={{ marginBottom: "2rem" }}>
          <h2>MedSpaBookings</h2>
          <span>Email Preferences</span>
        </div>

        {done ? (
          <div style={{ textAlign: "center", padding: "2rem 0" }} data-testid="unsubscribe-success">
            <CheckCircle2 size={64} style={{ color: "var(--success)", margin: "0 auto" }} />
            <h3 style={{ marginTop: "1rem" }}>You're Unsubscribed</h3>
            <p style={{ marginTop: "0.5rem", color: "var(--text-secondary)" }}>
              {email} will no longer receive marketing emails from MedSpaBookings.
            </p>
          </div>
        ) : (
          <>
            <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
              <MailX size={40} style={{ color: "var(--muted-foreground)" }} />
              <p style={{ marginTop: "1rem", color: "var(--text-secondary)" }}>
                Enter your email to stop receiving marketing emails from MedSpaBookings.
              </p>
            </div>

            <form onSubmit={handleUnsubscribe}>
              <label>
                Email
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="unsubscribe-email-input"
                />
              </label>

              {error && (
                <div style={{
                  padding: "0.75rem",
                  background: "var(--error-bg)",
                  border: "1px solid var(--error)",
                  borderRadius: "6px",
                  color: "var(--error)",
                  marginBottom: "1rem",
                  fontSize: "0.875rem",
                }} data-testid="unsubscribe-error-message">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="primary-btn"
                disabled={submitting || !email}
                data-testid="unsubscribe-submit"
              >
                {submitting ? "Unsubscribing..." : "Unsubscribe"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
